import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Mail } from "lucide-react";
import Logo from "@/components/Logo";

export default function ParentLogin() {
  const { login, logout, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Veli Paneli - Renka"; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "parent") {
        await logout();
        setError("Bu giriş yalnızca veliler içindir.");
        return;
      }
      navigate("/veli", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Zaten girişliyse login ekranını gösterme (geri tuşu senaryosu) — yalnızca veli rolü
  // (Yanlış rol/logout akışının çalışabilmesi için parent dışı kullanıcılarda form kalır.)
  if (user && user.role === "parent") {
    return <Navigate to="/veli" replace />;
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-slate-950 px-4">
      <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-blue-600/30 blur-[120px]" />
      <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-violet-600/30 blur-[120px]" />

      <div className="relative z-10 w-full max-w-md" data-testid="parent-login-page">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 inline-flex items-center justify-center rounded-2xl bg-white px-6 py-4 shadow-lg shadow-violet-900/30">
            <Logo className="h-12 w-auto object-contain" alt="RENKA" />
          </div>
          <p className="text-sm font-medium tracking-wide text-slate-300">Veli Girişi</p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-300">E-posta</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" data-testid="parent-login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="veli@ornek.com" required className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-violet-500" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-300">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="password" type="password" data-testid="parent-login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-violet-500" />
              </div>
            </div>
            {error && <p data-testid="parent-login-error" className="text-sm font-medium text-red-400">{error}</p>}
            <Button type="submit" data-testid="parent-login-submit" disabled={loading} className="h-12 w-full rounded-xl bg-blue-600 text-base font-semibold text-white hover:bg-blue-700 active:scale-[0.98]">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Giriş Yap"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
